import {
  Component,
  RESPONSE_INIT,
  computed,
  inject,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import type { ResolveFn } from '@angular/router'
import { ActivatedRoute, RedirectCommand, Router } from '@angular/router'

import * as Sentry from '@sentry/angular'
import { TranslatePipe } from '@ngx-translate/core'
import type { Observable } from 'rxjs'
import {
  BehaviorSubject,
  catchError,
  forkJoin,
  map,
  of,
  switchMap,
  take,
} from 'rxjs'

import { ArticlesService } from '../../../api/articles-service/articles.service'
import { EditionsService } from '../../../api/editions-service/editions.service'
import { hasGraphQLErrorStatus } from '../../../api/graphql-error-status'
import { MagazineService } from '../../../api/magazine-service/magazine.service'
import { MagazineOptions } from '../../../api/queries'
import type {
  ArticlesDto,
  EditionDto,
  MagazineDto,
} from '../../../api/responses'
import { ArticleList } from '../../../components/article-list/article-list'
import { BreadcrumbsComponent } from '../../../components/breadcrumbs/breadcrumbs.component'
import { ArticleTypeSelectFilter } from '../../../components/filter-group/components/filters/article-type-select-filter'
import type { stringMap } from '../../../components/filter-group/components/filters/components/types'
import { FilterGroup } from '../../../components/filter-group/filter-group'
import { AuthService } from '../../../services/auth/auth.service'
import type { SeoRouteMeta } from '../../../shared/seo/seo.types'
import { firstImage, firstText } from '../../../shared/seo/seo.util'
import type {
  ArticleWithNullableRefs,
  BreadcrumbItem,
  YearChip,
} from '../../../types/types'
import { magazineIdFromRoute } from '../../magazines-and-journals/magazines-and-journals-detail-page/magazines-and-journals-detail-page.component'
import { YearFilterDialogComponent } from '../../magazines-and-journals/magazines-and-journals-detail-page/components/year-filter-dialog/year-filter-dialog.component'
import { EditionHeaderComponent } from './components/edition-header/edition-header.component'
import { PreviousEditionsSectionComponent } from './components/previous-editions-section/previous-editions-section.component'

export interface EditionDetailData {
  edition: EditionDto | null
  magazine: MagazineDto | null
  articles: ArticlesDto[]
  editions: EditionDto[]
  /**
   * Set when the edition doesn't exist and there is no magazine to send the
   * reader to.
   */
  notFound?: boolean
}

const NO_EDITION_DETAIL: EditionDetailData = {
  edition: null,
  magazine: null,
  articles: [],
  editions: [],
}

function selectionEssenceRedirect(
  router: Router,
  magazineId: number,
  slug: string
): RedirectCommand {
  return new RedirectCommand(
    router.createUrlTree([
      '/magazines/selection-essence/detail',
      magazineId,
      slug,
    ])
  )
}

// Fetched in a resolver so the server render waits for it; see
// magazineDetailResolver. Everything after the edition is fetched in parallel.
export const editionDetailResolver: ResolveFn<EditionDetailData> = route => {
  const editionsService = inject(EditionsService)
  const magazineService = inject(MagazineService)
  const articlesService = inject(ArticlesService)
  const authService = inject(AuthService)
  const router = inject(Router)
  const responseInit = inject(RESPONSE_INIT, { optional: true })

  // On the nested magazine route the last `:id` wins, which is the edition's.
  const editionId = parseInt(route.paramMap.get('id') ?? '', 10)
  if (isNaN(editionId)) return of(NO_EDITION_DETAIL)

  // The server renders anonymously, so a logged-in reader needs their own
  // copy of the magazine for `user_access` (subscribe button, PDF download).
  const magazinePolicy = authService.isAuthenticated()
    ? 'network-only'
    : 'cache-first'

  return editionsService.getEditionById(editionId).pipe(
    take(1),
    switchMap(
      (
        edition: EditionDto | null
      ): Observable<EditionDetailData | RedirectCommand> => {
        if (!edition?.magazine) return of(NO_EDITION_DETAIL)

        const magazineId = edition.magazine.id

        return forkJoin({
          magazine: magazineService
            .getMagazineById(magazineId, new MagazineOptions(), magazinePolicy)
            .pipe(
              take(1),
              map(magazine => magazine ?? null),
              catchError((error: unknown) => {
                console.error('Error fetching edition magazine:', error)
                Sentry.captureException(error)
                return of(null)
              })
            ),
          articles: articlesService
            .getArticlesByEditionId(editionId)
            .pipe(take(1)),
          editions: editionsService.getEditionsByMagazineId(magazineId).pipe(
            take(1),
            map(editions => editions.items),
            catchError((error: unknown) => {
              console.error('Error fetching magazine editions:', error)
              Sentry.captureException(error)
              return of([])
            })
          ),
        }).pipe(
          map(({ magazine, articles, editions }) =>
            magazine?.selectionAndEssence
              ? selectionEssenceRedirect(router, magazine.id, magazine.slug)
              : { edition, magazine, articles, editions }
          )
        )
      }
    ),
    catchError((error: unknown) => {
      // A missing edition (S&E magazines have none) sends the reader to its magazine.
      const magazineId = magazineIdFromRoute(route)
      const slug = route.paramMap.get('slug')
      const isNotFound = hasGraphQLErrorStatus(error, 404)
      if (isNotFound && magazineId !== null && slug) {
        return of(
          new RedirectCommand(
            router.createUrlTree(['/magazines/detail', magazineId, slug])
          )
        )
      }
      // Without a slug this is the short /editions/:id route.
      if (isNotFound) {
        if (responseInit) responseInit.status = 404
        return of({ ...NO_EDITION_DETAIL, notFound: true })
      }

      console.error('Error fetching edition details:', error)

      Sentry.captureException(error)
      return of(NO_EDITION_DETAIL)
    })
  )
}

/** Head metadata for an edition, registered as `data: { seo: editionSeo }`. */
export const editionSeo: SeoRouteMeta = route => {
  const detail = route.data['editionDetail'] as EditionDetailData | undefined
  const edition = detail?.edition
  const magazine = detail?.magazine
  if (!edition || !magazine) return null

  const editionLabel = [
    magazine.title,
    edition.year,
    edition.edition_version ? `nr. ${edition.edition_version}` : null,
  ]
    .filter(Boolean)
    .join(' ')

  return {
    title: editionLabel,
    description: firstText(
      detail.articles.map(article => article.title).join(' · '),
      magazine.meta?.description,
      magazine.subtitle
    ),
    image: firstImage(magazine.meta?.image, {
      url: magazine.media?.cover?.url,
    }),
    canonicalPath: `/magazines/detail/${String(magazine.id)}/${encodeURIComponent(magazine.slug)}/edition/${String(edition.id)}`,
  }
}

@Component({
  selector: 'dhp-edition-overview',
  templateUrl: './edition-overview.component.html',
  styleUrls: ['./edition-overview.component.scss'],
  imports: [
    BreadcrumbsComponent,
    EditionHeaderComponent,
    ArticleList,
    PreviousEditionsSectionComponent,
    YearFilterDialogComponent,
    TranslatePipe,
    FilterGroup,
    ArticleTypeSelectFilter,
  ],
})
export class EditionOverviewComponent {
  private readonly route = inject(ActivatedRoute)

  readonly magazine = signal<MagazineDto | undefined>(undefined)
  readonly edition = signal<EditionDto | undefined>(undefined)
  readonly articles = signal<ArticleWithNullableRefs[]>([])
  readonly breadcrumbItems = signal<BreadcrumbItem[]>([])
  readonly hasPreviousEditions = signal(false)
  readonly notFound = signal(false)

  readonly selectedArticleTypes = signal<Set<string> | null>(null)
  readonly filteredArticles = computed(() => {
    const types = this.selectedArticleTypes()
    const articles = this.articles()

    if (!types || types.size === 0) return articles

    return articles.filter(
      article => article.article_type && types.has(article.article_type.name)
    )
  })

  // Properties for magazine-tabs
  readonly years = signal<YearChip[]>([])
  readonly editions = signal<EditionDto[]>([])
  filteredEditions: EditionDto[] = []
  private readonly selectedYear$ = new BehaviorSubject<YearChip | null>(null)
  private _isYearFilterOpen = false

  constructor() {
    this.route.data
      .pipe(
        map(
          data =>
            (data['editionDetail'] as EditionDetailData | undefined) ??
            NO_EDITION_DETAIL
        ),
        takeUntilDestroyed()
      )
      .subscribe(detail => {
        this.applyEditionDetail(detail)
      })
  }

  get yearSelected(): YearChip | null {
    return this.selectedYear$.value
  }

  set yearSelected(value: YearChip | null) {
    this.selectedYear$.next(value)
    this.updateFilteredEditions()
  }

  get isYearFilterOpen(): boolean {
    return this._isYearFilterOpen
  }

  set isYearFilterOpen(value: boolean) {
    this._isYearFilterOpen = value
  }

  generateYearsFromEditions(): void {
    const yearCounts = this.editions().reduce<Record<string, number>>(
      (acc, edition) => {
        const year = edition.year?.toString() ?? ''
        if (year) {
          acc[year] = (acc[year] ?? 0) + 1
        }
        return acc
      },
      {}
    )

    this.years.set(
      Object.keys(yearCounts)
        .map(year => ({
          label: year,
          value: year ? parseInt(year, 10) : null,
          count: yearCounts[year],
        }))
        .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
    )

    // Select the first year by default
    if (this.years().length > 0) {
      this.yearSelected = this.years()[0]
    }
  }

  updateFilteredEditions(): void {
    const selectedYear = this.yearSelected?.value

    if (selectedYear && this.editions().length > 0) {
      this.filteredEditions = this.editions().filter(edition => {
        return edition.year === selectedYear
      })
    } else {
      this.filteredEditions = [...this.editions()]
    }
  }

  onYearSelectedChange(year: YearChip | null): void {
    this.yearSelected = year
  }

  onArticleTypesChange(types: Set<stringMap> | null): void {
    if (!types) {
      this.selectedArticleTypes.set(null)
      return
    }

    this.selectedArticleTypes.set(new Set(Array.from(types, t => t.key)))
  }

  onResetArticleTypeFilter(): void {
    this.selectedArticleTypes.set(null)
  }

  removeArticleType(type: string): void {
    this.selectedArticleTypes.update(types => {
      if (!types) return types

      const updated = new Set(types)
      updated.delete(type)

      return updated.size > 0 ? updated : null
    })
  }

  private applyEditionDetail({
    edition,
    magazine,
    articles,
    editions,
    notFound,
  }: EditionDetailData): void {
    this.notFound.set(notFound ?? false)
    this.edition.set(edition ?? undefined)
    this.magazine.set(magazine ?? undefined)
    this.articles.set(articles as ArticleWithNullableRefs[])
    this.editions.set(editions)
    this.hasPreviousEditions.set(editions.length > 1)
    this.generateYearsFromEditions()
    this.updateFilteredEditions()
    this.updateBreadcrumbs()
  }

  private updateBreadcrumbs(): void {
    const magazine = this.magazine()
    const edition = this.edition()
    if (!magazine || !edition) {
      this.breadcrumbItems.set([])
      return
    }

    this.breadcrumbItems.set([
      {
        label: 'components.breadcrumbs.home',
        url: '/',
      },
      {
        label: 'components.breadcrumbs.magazines',
        url: '/magazines',
      },
      {
        label: magazine.abbreviation ?? magazine.title ?? '',
        url: `/magazines/detail/${String(magazine.id)}/${magazine.slug}`,
      },
      {
        label: `components.breadcrumbs.editionWithDetails`,
        url: '',
        params: {
          year: edition.year?.toString() ?? '',
          version: edition.edition_version?.toString() ?? '',
        },
      },
    ])
  }
}
